import { useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { ArrowRight, CheckCircle, Mail01 } from "@untitledui/icons";
import { motion, useScroll, useSpring } from "motion/react";
import { Button } from "@/components/base/buttons/button";
import { LanguageSelector } from "@/components/application/language-selector/language-selector";
import { VersionFooter } from "@/components/base/version-footer/version-footer";
import { LandingFeaturesShowcase } from "@/components/marketing/landing/features-showcase";
import { LandingHero } from "@/components/marketing/landing/landing-hero";
import { LandingProblemSection } from "@/components/marketing/landing/problem-section";
import { World3DSection } from "@/components/marketing/landing/world-3d-section";
import { useMe, getRedirectPath } from "@/hooks/api/use-auth";

export const LandingPage = () => {
    const { t } = useTranslation(["pages:landing", "pages:legal"]);
    const { data: currentUser } = useMe();
    const featuresSectionRef = useRef<HTMLElement | null>(null);

    const chapters = useMemo(
        () => [
            { id: "problem", label: t("chapters.problem") },
            { id: "solution", label: t("chapters.solution") },
            { id: "features", label: t("chapters.features") },
            { id: "world", label: t("chapters.world") },
            { id: "pricing", label: t("chapters.pricing") },
            { id: "cta", label: t("chapters.cta") },
        ],
        [t],
    );

    const { scrollYProgress: featuresProgress } = useScroll({
        target: featuresSectionRef,
        // Progress 0 quando la testata è verso il centro dello schermo; 1 in uscita in alto (timeline più “leggibile”)
        offset: ["start center", "end start"],
    });
    const progressScaleX = useSpring(featuresProgress, { damping: 28, stiffness: 120 });

    const env = import.meta.env.VITE_ENV;
    const hasEnvironmentBanner = env !== "production";
    const fixedTopBar =
        hasEnvironmentBanner && (env === "development" || env === "staging" || import.meta.env.DEV);

    return (
        <div className={`relative min-h-screen bg-secondary ${hasEnvironmentBanner ? "pt-7" : ""}`}>
            <motion.div
                aria-hidden
                className="fixed left-0 top-0 z-40 h-1 w-full origin-left bg-brand-500"
                style={{ scaleX: progressScaleX }}
            />
            <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
                <div className="absolute -right-32 -top-24 h-72 w-72 rounded-full bg-brand-500/20 blur-3xl motion-safe:animate-blob" />
                <div className="absolute -left-24 top-2/5 h-64 w-64 rounded-full bg-blue-500/20 blur-3xl motion-safe:animate-blob motion-safe:animation-delay-2000" />
                <div className="absolute bottom-0 right-1/4 h-80 w-80 rounded-full bg-purple-500/20 blur-3xl motion-safe:animate-blob motion-safe:animation-delay-4000" />
            </div>

            <header
                className={`z-30 border-b border-secondary_alt bg-primary/80 backdrop-blur-sm ${
                    fixedTopBar ? "fixed left-0 right-0 top-7" : "sticky top-0"
                }`}
            >
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div className="flex h-16 items-center justify-between gap-2 sm:gap-4">
                        <a href="/" className="min-w-0 shrink">
                            <img
                                src="/images/logo-full.svg"
                                alt="EveMind"
                                className="h-8 w-auto max-w-[min(11rem,46vw)] sm:h-9 sm:max-w-none"
                            />
                        </a>
                        <nav className="hidden lg:flex items-center gap-5">
                            {chapters.map((chapter) => (
                                <a
                                    key={chapter.id}
                                    href={`#${chapter.id}`}
                                    className="text-sm text-tertiary transition-colors hover:text-primary"
                                >
                                    {chapter.label}
                                </a>
                            ))}
                        </nav>
                        <div className="flex shrink-0 items-center gap-1.5 sm:gap-3">
                            <Button
                                href="/contact"
                                size="sm"
                                color="secondary"
                                iconLeading={Mail01}
                                aria-label={t("hero.ctaContact")}
                                className="sm:hidden"
                            />
                            <Button
                                href="/contact"
                                size="sm"
                                color="secondary"
                                className="hidden sm:inline-flex"
                            >
                                {t("hero.ctaContact")}
                            </Button>
                            {currentUser ? (
                                <Button href={getRedirectPath(currentUser) ?? "/auth/login"} size="sm" color="primary">
                                    {t("hero.ctaGoToApp")}
                                </Button>
                            ) : (
                                <Button href="/auth/login" size="sm" color="primary">
                                    {t("hero.ctaLogin")}
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            </header>

            {fixedTopBar ? <div className="h-16 shrink-0" aria-hidden /> : null}

            <main className="relative z-10">
                <LandingHero />
                <LandingProblemSection />

                <section id="solution" className="py-16 sm:py-24">
                    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                        <div className="grid gap-8 lg:grid-cols-2 lg:items-center">
                            <div className="space-y-5">
                                <p className="text-sm font-semibold uppercase tracking-wider text-brand-600">{t("solution.label")}</p>
                                <h2 className="text-3xl font-semibold text-primary sm:text-4xl">{t("solution.title")}</h2>
                                <p className="text-base text-quaternary sm:text-lg">{t("solution.description")}</p>
                                <ul className="space-y-3">
                                    {(["ingest", "decide", "act"] as const).map((key) => (
                                        <li key={key} className="flex items-start gap-3 rounded-xl border border-secondary_alt bg-primary/75 p-3">
                                            <CheckCircle className="mt-0.5 size-5 text-brand-600" />
                                            <div>
                                                <p className="font-medium text-primary">{t(`solution.bullets.${key}.title`)}</p>
                                                <p className="text-sm text-quaternary">{t(`solution.bullets.${key}.description`)}</p>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div className="rounded-3xl border border-secondary_alt bg-primary/80 p-6">
                                <p className="text-sm uppercase tracking-wide text-quaternary">{t("solution.timelineLabel")}</p>
                                <div className="mt-5 space-y-4">
                                    {(["capture", "validate", "predict", "notify"] as const).map((key) => (
                                        <div key={key} className="rounded-xl border border-secondary_alt bg-secondary p-4">
                                            <p className="text-xs uppercase tracking-wide text-brand-600">{t(`solution.timeline.${key}.time`)}</p>
                                            <p className="mt-1 text-base font-medium text-primary">{t(`solution.timeline.${key}.title`)}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section
                    id="features"
                    ref={featuresSectionRef}
                    className="scroll-mt-24 overflow-hidden py-12 sm:py-16 min-h-[82svh] sm:min-h-[88svh] flex flex-col justify-center sm:scroll-mt-28"
                >
                    <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
                        <div className="mx-auto max-w-3xl text-center">
                            <p className="text-sm font-semibold uppercase tracking-wider text-brand-600">{t("features.label")}</p>
                            <h2 className="mt-3 text-3xl font-semibold text-primary sm:text-4xl">{t("features.title")}</h2>
                            <p className="mt-4 text-base text-quaternary sm:text-lg">{t("features.description")}</p>
                        </div>
                        <LandingFeaturesShowcase />
                    </div>
                </section>

                <World3DSection />

                <section id="pricing" className="py-16 sm:py-24">
                    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                        <div className="rounded-3xl border border-secondary_alt bg-primary/85 p-6 sm:p-8 lg:p-10">
                            <p className="text-sm font-semibold uppercase tracking-wider text-brand-600">{t("pricing.label")}</p>
                            <h2 className="mt-3 text-3xl font-semibold text-primary sm:text-4xl">{t("pricing.title")}</h2>
                            <p className="mt-4 max-w-3xl text-base text-quaternary sm:text-lg">{t("pricing.description")}</p>
                            <div className="mt-8 grid gap-4 sm:grid-cols-3">
                                {(["volume", "operations", "integration"] as const).map((key) => (
                                    <div key={key} className="rounded-2xl border border-secondary_alt bg-secondary p-4">
                                        <p className="text-sm font-semibold text-primary">{t(`pricing.cards.${key}.title`)}</p>
                                        <p className="mt-1 text-sm text-quaternary">{t(`pricing.cards.${key}.description`)}</p>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-8 flex flex-wrap gap-3">
                                <Button href="/contact" size="xl" color="primary" iconTrailing={ArrowRight}>
                                    {t("pricing.ctaPrimary")}
                                </Button>
                                <Button href="/auth/login" size="xl" color="secondary">
                                    {t("pricing.ctaSecondary")}
                                </Button>
                            </div>
                        </div>
                    </div>
                </section>

                <section id="cta" className="pb-16 pt-8 sm:pb-24">
                    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                        <div className="rounded-3xl border border-brand-500/30 bg-brand-500/10 p-8 text-center sm:p-12">
                            <h2 className="text-3xl font-semibold text-primary sm:text-4xl">{t("finalCta.title")}</h2>
                            <p className="mx-auto mt-4 max-w-2xl text-base text-quaternary sm:text-lg">{t("finalCta.description")}</p>
                            <div className="mt-8 flex flex-wrap justify-center gap-3">
                                <Button href="/auth/login" size="xl" color="primary" iconTrailing={ArrowRight}>
                                    {t("finalCta.ctaPrimary")}
                                </Button>
                                <Button href="/contact" size="xl" color="secondary" iconLeading={Mail01}>
                                    {t("finalCta.ctaSecondary")}
                                </Button>
                            </div>
                        </div>
                    </div>
                </section>
            </main>

            <footer className="border-t border-secondary_alt bg-primary/95 py-10">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col gap-6 border-t border-secondary_alt pt-8 sm:flex-row sm:items-center sm:justify-between">
                        <img src="/images/logo-full.svg" alt="EveMind" className="h-10 w-auto" />
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-quaternary">
                            <Link
                                to="/terms"
                                className="hover:text-primary transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 rounded"
                            >
                                {t("footer.terms", { ns: "pages:legal" })}
                            </Link>
                            <span className="text-quaternary/60" aria-hidden>
                                |
                            </span>
                            <Link
                                to="/privacy"
                                className="hover:text-primary transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 rounded"
                            >
                                {t("footer.privacy", { ns: "pages:legal" })}
                            </Link>
                        </div>
                        <div className="flex items-center gap-3">
                            <LanguageSelector showLabel={false} />
                            <p className="text-sm text-quaternary">© {new Date().getFullYear()} Eve Mind</p>
                        </div>
                    </div>
                    <div className="mt-4">
                        <VersionFooter />
                    </div>
                </div>
            </footer>
        </div>
    );
};

